import { Divider, Tab, Tabs } from "@mui/material";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiSideModal from "components/base/UiSideModal";
import UiTypography from "components/base/UiTypography";
import MultiSelectExtension from "components/Shared/MultiSelectExtension/MultiSelectExtension";
import AdditionalCostOptions from "modules/additionalCosts/AdditionalCostOptions";
import AdditionalCostForm from "components/organization/AdditionalCostForm";
import { CreateAdditionalCostInput } from "generated/graphql";
import useCreateAdditionalCost from "hooks/organization/useCreateAdditionalCost";
import { useCallback, useState } from "react";
import { draftColors } from "./draftDesignTokens";
import { drawerTabStyles } from "./drawerTabStyles";

type AddAdditionalCostDrawerProps = {
  open: boolean;
  onClose: () => void;
  onAdd: (ids: string[]) => Promise<void>;
};

export default function AddAdditionalCostDrawer({
  open,
  onClose,
  onAdd,
}: AddAdditionalCostDrawerProps) {
  const [createAdditionalCost, { loading, getData }] = useCreateAdditionalCost();
  const [additionalCosts, setAdditionalCosts] = useState<string[]>([]);
  const [additionalCostId, setAdditionalCostId] = useState<string | null>(null);
  const [openTab, setOpenTab] = useState(0);

  const handleCreate = useCallback(
    async (values: CreateAdditionalCostInput) => {
      const formattedInput = {
        ...values,
        feeAmount: +values.feeAmount.toFixed(2),
        cost: Math.floor(values.cost * 100),
      };
      const result = await createAdditionalCost({ variables: { input: formattedInput } });
      const data = getData(result);
      if (result.success) {
        await onAdd([data.id]);
        onClose();
      }
      return { success: result.success, data };
    },
    [createAdditionalCost, getData, onAdd, onClose]
  );

  const handleAdd = useCallback(async () => {
    if (additionalCosts.length > 0) {
      await onAdd(additionalCosts);
      setAdditionalCosts([]);
      onClose();
    }
  }, [additionalCosts, onAdd, onClose]);

  return (
    <UiSideModal open={open} onClose={onClose} title="Add Additional Cost" width="598px">
      <MDBox display="flex" flexDirection="column" height="100%">
        <MDBox px="24px" pt="8px">
          <Tabs value={openTab} onChange={(_, v) => setOpenTab(v)} sx={drawerTabStyles}>
            <Tab label="Select existing" />
            <Tab label="Create new" />
          </Tabs>
        </MDBox>

        <Divider sx={{ borderColor: draftColors.border, m: 0 }} />

        <MDBox p="24px" display="flex" flexDirection="column" gap="16px" flex={1}>
          {openTab === 0 && (
            <AdditionalCostOptions>
              {({
                additionalCostOptions,
                additionalCostsById,
                loading: optionsLoading,
                search,
                pagination,
              }) => (
                <MDBox>
                  <UiTypography uiVariant="heading-sm" sx={{ mb: "12px" }}>
                    Additional Cost(s)
                  </UiTypography>
                  <MultiSelectExtension
                    tags={additionalCosts}
                    setTags={setAdditionalCosts}
                    options={additionalCostOptions}
                    onChange={(value) => setAdditionalCostId(value as string)}
                    loading={optionsLoading}
                    pagination={pagination}
                    search={search}
                    id={additionalCostId}
                    label="Additional Cost"
                    nameCheckFn={additionalCostsById}
                  />
                  <MDBox display="flex" justifyContent="flex-end">
                    <UiButton
                      uiVariant="primary-blue"
                      onClick={handleAdd}
                      disabled={additionalCosts.length < 1}
                    >
                      Add selected
                    </UiButton>
                  </MDBox>
                </MDBox>
              )}
            </AdditionalCostOptions>
          )}

          {openTab === 1 && (
            <AdditionalCostForm
              loading={loading}
              onSubmit={handleCreate}
              action="create"
              isOnModal
            />
          )}
        </MDBox>
      </MDBox>
    </UiSideModal>
  );
}
